import React, { useState } from 'react';
import { DialogClose, DialogContent, DialogTitle } from '../shared/Dialog';
import { OrderApi, Ticket, TicketStatus, AvailableTicket } from '@/lib/OrderApi';
import { ImportInfo } from './ImportInfo';
import { Switch } from '../catalyst/switch';
import { Money } from '@/lib/money';
import { useLanguage } from '@/providers/LanguageProvider';
import { RiFileCopyLine, RiWhatsappLine } from '@remixicon/react';
import copy from 'copy-to-clipboard';
import { useEvent } from '@/providers/EventProvider';
import * as m from "@/paraglide/messages.js"
import { MainButton } from '../shared/MainButton';

interface ShareTicketModalProps {
    orderId: string,
    ticket: Ticket,
    onSuccess: () => void,
}

function isSharedTicket(ticket: Ticket): ticket is import('@/lib/OrderApi').SharedTicketStatus {
  return ticket.ticketStatus === TicketStatus.shared && 'shareCode' in ticket;
}

function hasAllowPaidShare(ticket: Ticket): ticket is Ticket & { allowPaidShare: boolean } {
  return 'allowPaidShare' in ticket && typeof ticket.allowPaidShare === 'boolean';
}

const ShareTicketModal: React.FC<ShareTicketModalProps> = ({orderId, ticket, onSuccess}) => {

  const { currentLanguage: lang } = useLanguage();

  const [loading, setLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);

  const [paidShare, setPaidShare] = useState(false);

  const [inMemoryTicket, setTicket] = useState<Ticket>(ticket);

  const [name, setName] = useState('');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [emailNotifiedTo, setEmailNotifiedTo] = useState<string | null>(null);

  const [showCopied, setShowCopied] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const { event } = useEvent();

  const shareUrl = () => {
    if (!isSharedTicket(inMemoryTicket)) {
      return;
    }
    return `${window.location.origin}/ticket-share/${inMemoryTicket.shareCode}`;
  }

  const handleCopy = () => {
    const url = shareUrl();
    if (!url) return;
    copy(url, { message: m.share_ticket_link_copied() });
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 3000);
  };

  const handleWhatsapp = () => {
    const url = shareUrl();
    if (!url) return;
    const text = m.share_ticket_whatsapp_message({
      eventName: event?.name || '',
      url,
    });
    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank', 'noopener,noreferrer');
  };

  async function shareTicket(): Promise<void> {
    setShareError(null);

    if (name.trim() === '') {
      setShareError(m.share_ticket_name_required());
      return;
    }

    const emailTrimmed = recipientEmail.trim();
    if (emailTrimmed !== '' && !/^\S+@\S+\.\S+$/.test(emailTrimmed)) {
      setShareError(m.share_ticket_email_error());
      return;
    }

    setLoading(true);
    try {
      const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);
      const shareCode = await ticketApi.shareTicket(
        orderId,
        ticket.ticketId,
        name.trim(),
        paidShare,
        emailTrimmed || undefined,
        lang,
      );

      setTicket({
        ...ticket,
        ticketStatus: TicketStatus.shared,
        shareCode: shareCode,
        shareName: name,
        inviteeShouldPay: paidShare,
        onMarketplace: false,
        canResell: false,
      } as import('@/lib/OrderApi').SharedTicketStatus);
      setEmailNotifiedTo(emailTrimmed || null);
      onSuccess();
    } catch (error) {
      console.error(error);
      setShareError(m.share_ticket_error());
    } finally {
      setLoading(false);
    }
  }

  async function cancelShare(): Promise<void> {
    setCancelLoading(true);
    setCancelError(null);
    try {
      const ticketApi = new OrderApi(import.meta.env.VITE_API_URL);
      const result = await ticketApi.cancelShareTicket(orderId, ticket.ticketId);
      if (!result.success) {
        setCancelError(result.message || m.share_ticket_error());
        return;
      }
      setTicket({
        ...inMemoryTicket,
        ...ticket,
        ticketStatus: TicketStatus.available,
        wallet_download_url: null,
        pdf_download_url: null,
        barcode: '',
        sealed: false,
        availableFrom: null,
      } as AvailableTicket);
      onSuccess();
    } catch (error) {
      console.error(error);
      setCancelError(m.share_ticket_error());
    } finally {
      setCancelLoading(false);
    }
  }

  return (
      <DialogContent className='bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]'>
        <DialogTitle className='text-center mx-auto'>{ticket.productName} <span className='text-xs tracking-tight'> {ticket.numberOfProductType}/{ticket.totalOfProductType}</span></DialogTitle>
        {inMemoryTicket.ticketStatus === TicketStatus.shared && isSharedTicket(inMemoryTicket) && (
          <div>
            <p className="text-xl font-bold mt-6">
              {m.share_ticket_shared()}
            </p>

            <ImportInfo className='pt-4 pb-2'>
                {isSharedTicket(inMemoryTicket) && (
                  <>
                    <ImportInfo.Item>{m.share_ticket_link_created({ forName: inMemoryTicket.shareName !== undefined ? m.share_ticket_link_created_for({ name: inMemoryTicket.shareName }) : '' })}</ImportInfo.Item>
                    {emailNotifiedTo && (
                      <ImportInfo.Item>{m.share_ticket_email_sent_to({ email: emailNotifiedTo })}</ImportInfo.Item>
                    )}
                    <ImportInfo.Item>{m.share_ticket_friend_accept()}</ImportInfo.Item>
                    <ImportInfo.Item>{m.share_ticket_cannot_undo()}</ImportInfo.Item>
                    {inMemoryTicket.inviteeShouldPay && (
                      <ImportInfo.Item>{m.share_ticket_friend_pay({ price: Money.fromInteger(ticket.price.amount, ticket.price.currency).display(lang), fee: Money.fromInteger(ticket.serviceFee.amount, ticket.serviceFee.currency).display(lang) })}</ImportInfo.Item>
                    )}
                  </>
                )}
              </ImportInfo>

              <div className='mt-4'>
                <p className='text-sm font-semibold mb-2'>{m.share_ticket_method_title()}</p>
                <div className='grid grid-cols-2 gap-2'>
                  <button
                    type='button'
                    onClick={handleCopy}
                    className='flex flex-col items-center justify-center gap-1 p-3 rounded-md border border-[var(--shop-borderColor)] hover:bg-[var(--shop-ctaButtonColor)] hover:text-[var(--shop-ctaButtonTextColor)] transition-colors'
                  >
                    <RiFileCopyLine className='w-6 h-6' />
                    <span className='text-xs'>{m.share_ticket_method_copy()}</span>
                  </button>
                  <button
                    type='button'
                    onClick={handleWhatsapp}
                    className='flex flex-col items-center justify-center gap-1 p-3 rounded-md border border-[var(--shop-borderColor)] hover:bg-[var(--shop-ctaButtonColor)] hover:text-[var(--shop-ctaButtonTextColor)] transition-colors'
                  >
                    <RiWhatsappLine className='w-6 h-6' />
                    <span className='text-xs'>{m.share_ticket_method_whatsapp()}</span>
                  </button>
                </div>
                {showCopied && <div className='pt-2 text-xs opacity-70 text-center'>{m.share_ticket_link_copied()}</div>}
              </div>

              <button
                className='bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md mt-4 disabled:opacity-50'
                onClick={cancelShare}
                disabled={cancelLoading}
              >
                {cancelLoading ? m.share_ticket_cancelling() : m.share_ticket_cancel()}
              </button>
              {cancelError && (
                <div className="text-red-500 text-sm mt-2 text-center">{cancelError}</div>
              )}

              <DialogClose asChild>
                <button className='bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)] p-2 rounded-md text-center block w-full hover:opacity-90 transition-all duration-300 hover:shadow-md mt-4'>
                    {m.close()}
                </button>
            </DialogClose>
          </div>
        )}
        {(inMemoryTicket.ticketStatus === TicketStatus.claimable || inMemoryTicket.ticketStatus === TicketStatus.available) && (
          <div className="">
            <p className="text-xl font-bold mt-6">
              {m.share_ticket_title()}
            </p>

            <ol className='flex items-start gap-2 pt-4 pb-2 text-xs leading-tight' data-testid='share-steps'>
              {[m.share_ticket_step_name(), m.share_ticket_step_link(), m.share_ticket_step_send()].map((step, i) => (
                <li key={i} className='flex-1 flex flex-col items-center text-center gap-1'>
                  <span className='w-6 h-6 rounded-full flex items-center justify-center font-bold bg-[var(--shop-ctaButtonColor)] text-[var(--shop-ctaButtonTextColor)]'>{i + 1}</span>
                  <span>{step}</span>
                </li>
              ))}
            </ol>
            <ImportInfo className='pb-2'>
              {hasAllowPaidShare(inMemoryTicket) && inMemoryTicket.allowPaidShare && (
                <ImportInfo.Item>{m.share_ticket_free_or_paid()}</ImportInfo.Item>
              )}
              <ImportInfo.Item>{m.share_ticket_accepts_info()}</ImportInfo.Item>
            </ImportInfo>

              {hasAllowPaidShare(inMemoryTicket) && inMemoryTicket.allowPaidShare && (
                <div className='flex items-center gap-2 py-2'>
                  <Switch className='shrink-0' color="sky" name="paidShare" checked={paidShare} onChange={setPaidShare}/>
                  <span className='text-xs tracking-tight'>{m.share_ticket_paid_share_info({ price: Money.fromInteger(ticket.price.amount, ticket.price.currency).display(lang), fee: Money.fromInteger(ticket.serviceFee.amount, ticket.serviceFee.currency).display(lang) })}</span>
                </div>
              )}

              <label htmlFor="name" className='block text-xs opacity-70 mt-4'>
                {m.share_ticket_name_label()}
              </label>
              <input
                  id="name"
                  name="name"
                  type="text"
                  placeholder={m.share_ticket_name_placeholder()}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="mt-1 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-[var(--shop-borderColor)] placeholder:text-[var(--shop-textColor)] placeholder:opacity-40 focus:ring-2 focus:ring-inset focus:ring-[var(--shop-ctaButtonColor)] sm:text-sm sm:leading-6 bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]"
                />

              <label htmlFor="recipientEmail" className='block text-xs opacity-70 mt-4'>
                {m.share_ticket_email_optional_label()}
              </label>
              <input
                  id="recipientEmail"
                  name="recipientEmail"
                  type="email"
                  placeholder={m.share_ticket_email_placeholder()}
                  value={recipientEmail}
                  onChange={(e) => { setRecipientEmail(e.target.value); setShareError(null); }}
                  className="mt-1 block w-full rounded-md border-0 py-1.5 shadow-sm ring-1 ring-inset ring-[var(--shop-borderColor)] placeholder:text-[var(--shop-textColor)] placeholder:opacity-40 focus:ring-2 focus:ring-inset focus:ring-[var(--shop-ctaButtonColor)] sm:text-sm sm:leading-6 bg-[var(--shop-panelBackgroundColor)] text-[var(--shop-textColor)]"
                />

              <MainButton disabled={loading} loading={loading} onClick={() => shareTicket()} className="mt-4">
                <div className="text-lg font-bold leading-7">{loading ? m.share_ticket_sharing() : m.share_ticket_share_button()}</div>
              </MainButton>
              {shareError && (
                <div className="text-red-500 text-sm mt-2 text-center">{shareError}</div>
              )}
          </div>
        )}
      </DialogContent>
  );
};

export default ShareTicketModal;
